import React, { useState } from "react";
import Header from "../components/Header";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { errMsg, inputCls, Panel } from "../components/admin/ui";
import TwoFactorPanel from "../components/TwoFactorPanel";
import BuyerBalance from "../components/BuyerBalance";
import SoundSettings from "../components/SoundSettings";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export default function Account() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error(t("acct.pw_mismatch")); return; }
    setSaving(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      localStorage.setItem("mql_token", r.data.token);
      setForm(EMPTY);
      toast.success(t("acct.pw_changed"));
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">{t("acct.label")}</div>
        <h1 className="text-3xl font-bold text-[#F5F3ED] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{t("acct.title")}</h1>

        <Panel title={t("acct.profile")} testId="account-profile-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-[#A8A8AD]">{t("auth.name")}</div><div data-testid="account-name" className="text-[#F5F3ED] mt-1">{user.name}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-[#A8A8AD]">{t("auth.email")}</div><div data-testid="account-email" className="text-[#F5F3ED] mt-1 break-all">{user.email}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-[#A8A8AD]">{t("acct.role")}</div><div data-testid="account-role" className="text-[#D4AF37] mt-1">{t(`role.${user.role}`)}</div></div>
          </div>
        </Panel>

        {user.role === "buyer" && <BuyerBalance />}

        {user.role !== "supplier" && <SoundSettings />}

        <TwoFactorPanel />

        <Panel title={t("acct.change_pw")} testId="account-password-panel">
          <form onSubmit={submit} className="space-y-4">
            {[
              ["current_password", t("acct.current_pw"), 1],
              ["new_password", t("acct.new_pw"), 8],
              ["confirm", t("acct.confirm_pw"), 8],
            ].map(([k, label, min]) => (
              <label key={k} className="block">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{label}</span>
                <input data-testid={`account-${k.replace("_", "-")}-input`} type="password" required minLength={min} value={form[k]} onChange={set(k)} className={inputCls} />
              </label>
            ))}
            <button data-testid="account-change-password-btn" type="submit" disabled={saving}
              className="bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-50 transition-colors font-bold rounded-sm px-6 py-2.5 text-sm">
              {saving ? t("acct.saving") : t("acct.save_pw")}
            </button>
          </form>
        </Panel>
      </div>
    </div>
  );
}
